import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Loader2, ImageOff, RefreshCw, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { haptics } from '@/lib/haptics';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatGradingCompany } from '@/constants/gradingCompanies';
import type { UserItem } from '@/types/userItem';
import { useStockImageQueue } from './useStockImageQueue';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Photoless items to walk through. */
  items: UserItem[];
}

function buildDefaultQuery(item: UserItem): string {
  const parts = [item.title];
  const setId = (item.set_code || item.set_name || '').trim();
  if (setId && !item.title.toLowerCase().includes(setId.toLowerCase())) parts.push(setId);
  if (item.card_number) parts.push(`#${item.card_number}`);
  if (item.category === 'slabs' && item.grading_company) {
    const companyDisplay = formatGradingCompany(item.grading_company);
    parts.push(item.slab_grade ? `${companyDisplay} ${item.slab_grade}` : companyDisplay);
  }
  return parts.join(' ');
}

export function StockImageReviewQueueDialog({ open, onOpenChange, items }: Props) {
  const isMobile = useIsMobile();
  const queue = useStockImageQueue(items, open);
  const [queryOverride, setQueryOverride] = useState<string>('');
  const [flashUrl, setFlashUrl] = useState<string | null>(null);

  // Reset the editable query whenever the current item changes
  useEffect(() => {
    if (queue.current) setQueryOverride(buildDefaultQuery(queue.current));
    setFlashUrl(null);
  }, [queue.current?.id]);

  const handleTapCandidate = (imageUrl: string) => {
    setFlashUrl(imageUrl);
    haptics.tap();
    // Slight delay so the user sees a confirmation flash before advancing
    setTimeout(() => queue.apply(imageUrl), 120);
  };

  const handleClose = () => {
    queue.finish();
    const { applied, skipped, nomatch } = queue.summary;
    if (applied + skipped + nomatch > 0) {
      toast.success(`Applied ${applied} · Skipped ${skipped}${nomatch ? ` · No match ${nomatch}` : ''}`);
    }
    onOpenChange(false);
  };

  const body = (
    <div className="flex flex-col gap-4">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-medium">
            {queue.isDone ? 'All done' : `${Math.min(queue.index + 1, queue.total)} of ${queue.total}`}
          </span>
          <span className="tabular-nums">
            <span className="text-active-gold">{queue.summary.applied}</span> applied
            {queue.summary.skipped > 0 && <> · {queue.summary.skipped} skipped</>}
            {queue.summary.nomatch > 0 && <> · {queue.summary.nomatch} no-match</>}
          </span>
        </div>
        <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full bg-active-gold transition-all duration-300"
            style={{
              width: `${queue.total === 0 ? 0 : (queue.index / queue.total) * 100}%`,
            }}
          />
        </div>
      </div>

      {queue.isDone ? (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <div className="w-12 h-12 rounded-full bg-active-gold/15 border border-active-gold/30 flex items-center justify-center">
            <Check className="w-6 h-6 text-active-gold" />
          </div>
          <div className="text-[15px] font-semibold text-foreground">Review complete</div>
          <div className="text-sm text-muted-foreground max-w-xs">
            {queue.summary.applied} photo{queue.summary.applied === 1 ? '' : 's'} applied
            {queue.summary.skipped + queue.summary.nomatch > 0
              ? ` · ${queue.summary.skipped + queue.summary.nomatch} left untouched`
              : ''}
            .
          </div>
          <Button onClick={handleClose} className="rounded-full mt-2 px-6">
            Done
          </Button>
        </div>
      ) : queue.current ? (
        <>
          {/* Current item context */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
            <div className="text-[14px] font-semibold text-foreground leading-tight truncate">
              {queue.current.title}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {[queue.current.game, queue.current.set_name, queue.current.card_number ? `#${queue.current.card_number}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>

          {/* Editable query + refetch */}
          <div className="flex items-center gap-2">
            <Input
              value={queryOverride}
              onChange={(e) => setQueryOverride(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  queue.refetchCurrent(queryOverride);
                }
              }}
              placeholder="Search query"
              className="h-10 text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => queue.refetchCurrent(queryOverride)}
              disabled={queue.currentEntry?.status === 'loading'}
              className="h-10 w-10 shrink-0 rounded-full"
              aria-label="Refetch"
            >
              <RefreshCw className={cn('w-4 h-4', queue.currentEntry?.status === 'loading' && 'animate-spin')} />
            </Button>
          </div>

          {/* Candidate grid */}
          <div className="min-h-[240px]">
            {queue.currentEntry?.status === 'loading' && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-lg" />
                ))}
              </div>
            )}

            {queue.currentEntry?.status === 'error' && (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                <ImageOff className="w-6 h-6 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">Couldn't load candidates.</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => queue.refetchCurrent(queryOverride)}
                  className="rounded-full"
                >
                  Retry
                </Button>
              </div>
            )}

            {queue.currentEntry?.status === 'ready' &&
              (queue.currentEntry.candidates.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                  <ImageOff className="w-6 h-6 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">No stock candidates found.</div>
                  <Button size="sm" variant="outline" onClick={queue.noMatch} className="rounded-full">
                    Skip & continue
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {queue.currentEntry.candidates.map((c) => {
                    const isFlash = flashUrl === c.imageUrl;
                    return (
                      <button
                        key={c.imageUrl}
                        type="button"
                        onClick={() => handleTapCandidate(c.imageUrl)}
                        className={cn(
                          'group relative aspect-square rounded-lg overflow-hidden border transition-all',
                          'border-white/[0.06] hover:border-active-gold/60 active:scale-[0.97]',
                          isFlash && 'border-active-gold ring-2 ring-active-gold/60',
                        )}
                      >
                        <img
                          src={c.imageUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                        {isFlash && (
                          <div className="absolute inset-0 flex items-center justify-center bg-active-gold/25">
                            <Check className="w-6 h-6 text-active-gold drop-shadow" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={queue.skip}
              className="rounded-full text-muted-foreground hover:text-foreground"
            >
              Skip
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={queue.noMatch}
                className="rounded-full"
              >
                No good match
              </Button>
              <Button variant="ghost" onClick={handleClose} className="rounded-full">
                Finish
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => (v ? onOpenChange(true) : handleClose())}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="bg-chrome border-white/[0.08] rounded-t-3xl max-h-[90dvh] overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="mb-2">
            <SheetTitle className="text-left text-[15px]">Review stock photos</SheetTitle>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : handleClose())}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review stock photos</DialogTitle>
          <DialogDescription className="sr-only">
            Walk through photoless items and pick a stock photo. Tap a thumbnail to apply and advance.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
